import type { Proposal, ProposalSection } from '@/lib/templates/api';
import type { Choice, ReviewState } from './review-decisions';

export type SectionProgress = 'unchanged' | 'open' | 'decided';

export type KeyDecision =
  | { kind: 'choose'; choice: Choice }
  | { kind: 'conflict'; choice: 'keep' };

export function sectionProgress(state: ReviewState, section: ProposalSection): SectionProgress {
  if (!section.changed) return 'unchanged';
  const outcome = state.outcomes[section.key];
  if (outcome === 'applied' || outcome === 'kept') return 'decided';
  if (outcome === 'conflicted') return state.conflictChoices[section.key] !== undefined ? 'decided' : 'open';
  return state.choices[section.key] !== undefined ? 'decided' : 'open';
}

export function tally(
  state: ReviewState,
  proposal: Proposal,
): { changed: number; total: number; decided: number; toApply: number; allDecided: boolean } {
  const changedSections = proposal.sections.filter((section) => section.changed);
  const decided = changedSections.filter((section) => sectionProgress(state, section) === 'decided').length;
  const toApply = changedSections.filter(
    (section) => state.outcomes[section.key] === undefined && state.choices[section.key] === 'use',
  ).length;
  return {
    changed: changedSections.length,
    total: proposal.sections.length,
    decided,
    toApply,
    allDecided: changedSections.length > 0 && decided === changedSections.length,
  };
}

export function adjacentChange(proposal: Proposal, fromKey: string | null, step: 1 | -1): string | null {
  const changed = proposal.sections.filter((section) => section.changed);
  if (fromKey === null) return (step === 1 ? changed[0] : changed[changed.length - 1])?.key ?? null;
  const all = proposal.sections;
  const start = all.findIndex((section) => section.key === fromKey);
  for (let i = start + step; i >= 0 && i < all.length; i += step) {
    if (all[i]!.changed) return all[i]!.key;
  }
  return null;
}

export function nextOpen(state: ReviewState, proposal: Proposal, afterKey: string | null): string | null {
  const { sections } = proposal;
  const start = afterKey === null ? -1 : sections.findIndex((section) => section.key === afterKey);
  for (let offset = 1; offset <= sections.length; offset++) {
    const section = sections[(start + offset) % sections.length]!;
    if (sectionProgress(state, section) === 'open') return section.key;
  }
  return null;
}

export function decisionFor(
  state: ReviewState,
  section: ProposalSection,
  action: 'use' | 'keep',
): KeyDecision | null {
  if (!section.changed) return null;
  const outcome = state.outcomes[section.key];
  if (outcome === 'applied' || outcome === 'kept') return null;
  if (outcome === 'conflicted') return action === 'keep' ? { kind: 'conflict', choice: 'keep' } : null;
  return { kind: 'choose', choice: action };
}
